import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import type { ThemeMode } from '../../../src/theme/ThemeContext';
import {
  FUTURES_CARD_NARROW_WIDTH,
  FUTURES_CARD_TIGHT_WIDTH,
} from '../../worldcup/PicksView/FuturesCard.constants';
import { COMPETITION_FUTURES_PLATE } from './CompetitionFutures.constants';

/**
 * The club futures square: a country flag as the ground, a kit centred on top.
 *
 * A sibling of `getFuturesCardStyles`'s `mainFlagPlate` rather than an edit to it. That style
 * is shared with the World Cup card, which AC-15.10 keeps unchanged, and it deliberately sets
 * no alignment because its content is a full-bleed image. Centring there would move the World
 * Cup's plate too. Here the plate has two layers, so it owns both.
 */
export const getCompetitionFuturesPlateStyles = (
  theme: Theme,
  windowWidth: number,
  themeMode: ThemeMode = 'light',
) => {
  const narrow = windowWidth < FUTURES_CARD_NARROW_WIDTH;
  const tight = windowWidth < FUTURES_CARD_TIGHT_WIDTH;
  const isDark = themeMode === 'dark';

  // Same plate geometry as the World Cup's, read from the same breakpoints, so the two cards
  // still line up when they sit on screen one after the other.
  const plateW = tight
    ? COMPETITION_FUTURES_PLATE.WIDTH.TIGHT
    : narrow
      ? COMPETITION_FUTURES_PLATE.WIDTH.NARROW
      : COMPETITION_FUTURES_PLATE.WIDTH.DEFAULT;
  const plateH = Math.round(plateW * COMPETITION_FUTURES_PLATE.HEIGHT_RATIO);

  return StyleSheet.create({
    plate: {
      width: plateW,
      height: plateH,
      borderRadius: tight ? 10 : 12,
      overflow: 'hidden',
      backgroundColor: isDark ? theme.gray[800] : theme.gray[100],
      borderWidth: 1,
      borderColor: isDark ? theme.gray[600] : theme.gray[200],
      ...(Platform.OS === 'web' && {
        boxShadow: isDark
          ? ('0 2px 6px rgba(0,0,0,0.4)' as never)
          : ('0 2px 6px rgba(0,0,0,0.08)' as never),
      }),
    },
    /**
     * Absolute *and* sized. `absoluteFillObject` alone pins the four edges but sets no width or
     * height, which is enough for an SVG (no intrinsic size, so it stretches) and not enough for
     * the WebP the UEFA fallback is served as — that one draws at its intrinsic 500×500 and the
     * plate crops a corner of it.
     */
    flag: {
      ...StyleSheet.absoluteFillObject,
      width: '100%',
      height: '100%',
    },
    /**
     * A white Real Madrid kit on a white-and-red Spanish flag is unreadable without this.
     * Dark enough to seat the kit, light enough that the flag is still identifiable.
     */
    scrim: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: isDark
        ? COMPETITION_FUTURES_PLATE.SCRIM_DARK
        : COMPETITION_FUTURES_PLATE.SCRIM_LIGHT,
    },
    /** Centres the oversized UEFA asset so the plate clips its transparent margins away. */
    fallbackFlagLayer: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
    },
    fallbackFlag: {
      width: Math.round(plateW * COMPETITION_FUTURES_PLATE.FALLBACK_FLAG_OVERSCAN),
      height: Math.round(plateW * COMPETITION_FUTURES_PLATE.FALLBACK_FLAG_OVERSCAN),
    },
    kitLayer: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
};

/** Kit size that fits inside the plate with breathing room on every side. */
export function getCompetitionFuturesPlateKitSize(windowWidth: number): number {
  const narrow = windowWidth < FUTURES_CARD_NARROW_WIDTH;
  const tight = windowWidth < FUTURES_CARD_TIGHT_WIDTH;
  const plateW = tight
    ? COMPETITION_FUTURES_PLATE.WIDTH.TIGHT
    : narrow
      ? COMPETITION_FUTURES_PLATE.WIDTH.NARROW
      : COMPETITION_FUTURES_PLATE.WIDTH.DEFAULT;
  const plateH = Math.round(plateW * COMPETITION_FUTURES_PLATE.HEIGHT_RATIO);
  // SoccerKit renders at 0.88 × size tall, so solve for the height budget rather than the width.
  return Math.round((plateH * COMPETITION_FUTURES_PLATE.KIT_HEIGHT_FRACTION) / 0.88);
}
